import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Chip, Button, TextField,
  CircularProgress, Alert, Stack, Divider,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getPatientMessages, logPatientMessage } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';
import { openWhatsApp, fmtDateTime, MSG_TYPE_LABELS } from './patientProfileHelpers';

// ── Comunicaciones enviadas al paciente ───────────────────────────────────────
export const ComunicacionesTab = ({ patient }) => {
  const notify = useNotification();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [text, setText]         = useState('');
  const [sending, setSending]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setMessages((await getPatientMessages(patient.id)).data || []); } catch {}
    finally { setLoading(false); }
  }, [patient.id]);

  useEffect(() => { load(); }, [load]);

  const handleSendText = async () => {
    if (!patient.phone) { notify('El paciente no tiene teléfono registrado', 'warning'); return; }
    if (!text.trim()) return;
    openWhatsApp(patient.phone, text.trim());
    setSending(true);
    try {
      await logPatientMessage(patient.id, {
        channel: 'whatsapp',
        message_type: 'indicaciones',
        message_preview: text.trim().slice(0, 200),
      });
      setText('');
      await load();
    } catch {
      // El mensaje ya se abrió en WhatsApp (irreversible) — avisamos que
      // quedó afuera del historial en vez de tragarnos el error en silencio.
      notify('WhatsApp abierto, pero no se pudo registrar en el historial', 'warning');
    }
    finally { setSending(false); }
  };

  return (
    <Box>
      {/* Componer mensaje libre */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem', display: 'block', mb: 0.75 }}>
          Enviar por WhatsApp
        </Typography>
        {patient.phone ? (
          <>
            <TextField
              fullWidth multiline rows={3} size="small"
              placeholder="Escribí indicaciones, instrucciones o cualquier mensaje..."
              value={text} onChange={e => setText(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Button
              fullWidth variant="contained" size="small"
              startIcon={<WhatsAppIcon />}
              disabled={!text.trim() || sending}
              onClick={handleSendText}
              sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1EBE57' } }}
            >
              Enviar mensaje
            </Button>
          </>
        ) : (
          <Alert severity="warning" sx={{ fontSize: '0.78rem' }}>
            El paciente no tiene número de WhatsApp registrado.
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Historial */}
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem', display: 'block', mb: 1 }}>
        Historial de comunicaciones
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>
      ) : messages.length === 0 ? (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', py: 2, textAlign: 'center' }}>
          Sin mensajes enviados.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {messages.map(m => (
            <Box key={m.id} sx={{
              display: 'flex', gap: 1, p: 1.25,
              border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
              bgcolor: 'grey.50',
            }}>
              <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366', flexShrink: 0, mt: '2px' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                  <Chip
                    label={MSG_TYPE_LABELS[m.message_type] || m.message_type}
                    size="small"
                    sx={{ height: 18, fontSize: '0.62rem' }}
                  />
                  <Typography variant="caption" color="text.disabled">{fmtDateTime(m.sent_at)}</Typography>
                </Box>
                {m.message_preview && (
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', lineHeight: 1.4, wordBreak: 'break-word' }}>
                    {m.message_preview}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};
